import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { margin, marginSizes, colorConstants, formLabelScaleRatio } from 'theme/default'
import { shallowEqual } from 'tools/object'
import Div from 'components/Div'
import { P } from 'components/Typo'
import Input, { inputStyle, SimpleInput, LabelElement } from 'components/Input'
import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import { RadioGroup, Radio, RadioDot } from 'components/Radio'
import DatePicker, { SimpleDatePicker, DropdownDatePicker } from 'components/DatePicker'
import QuantitySelector from 'components/QuantitySelector'
import TelephoneInput from 'components/TelephoneInput'
import AddressSearchInput from 'components/AddressSearchInput'
import PasswordInput from 'components/PasswordInput'
import NumberInput from 'components/NumberInput'
import Select, { SelectWithLabel } from 'components/Select'
import { InlineNotification, notificationStates } from 'components/InlineNotification/InlineNotification'
import FormContext, { withFormContext } from './FormContext'
import withRoute from 'decorators/Route'

export const makeFormField = (Component) => withFormContext(
  class FieldComponent extends React.Component {
    static propTypes = {
      fieldValidateOnChange: PropTypes.bool,
      fieldValidateOnBlur: PropTypes.bool,
      form: PropTypes.object,
      resetOnUnmount: PropTypes.bool,
      resetValue: PropTypes.any,
      onChange: PropTypes.func,
      onBlur: PropTypes.func,
      name: PropTypes.string.isRequired,
      getValue: PropTypes.func
    }

    static defaultProps = {
      fieldValidateOnChange: true,
      fieldValidateOnBlur: true,
      resetOnUnmount: true,
      resetValue: null
    }

    constructor(props) {
      super(props)
      const { form, name, fieldValidateOnChange, fieldValidateOnBlur, getValue } = props
      const { values, errors, validateOnChange, validateOnBlur, touched, isSubmitting } = form
      this.state = {
        isSubmitting,
        value: typeof getValue === 'function' ? getValue(values, name) : values[name],
        fieldError: errors[name],
        fieldTouched: touched[name],
        shouldValidateOnChange: fieldValidateOnChange !== undefined ? fieldValidateOnChange : validateOnChange,
        shouldValidateOnBlur: fieldValidateOnBlur !== undefined ? fieldValidateOnBlur : validateOnBlur
      }
    }

    static getDerivedStateFromProps (nextProps) {
      const { form, name, fieldValidateOnChange, fieldValidateOnBlur, getValue } = nextProps
      const { values, errors, validateOnChange, validateOnBlur, touched, isSubmitting } = form
      return {
        isSubmitting,
        value: typeof getValue === 'function' ? getValue(values, name) : values[name],
        fieldError: errors[name],
        fieldTouched: touched[name],
        shouldValidateOnChange: fieldValidateOnChange !== undefined ? fieldValidateOnChange : validateOnChange,
        shouldValidateOnBlur: fieldValidateOnBlur !== undefined ? fieldValidateOnBlur : validateOnBlur
      }
    }

    shouldComponentUpdate ({ form: nextForm, ...nextProps }, nextState) {
      const { form, ...props } = this.props
      return !shallowEqual(props, nextProps) || !shallowEqual(this.state, nextState)
    }

    componentWillUnmount () {
      const { name, resetOnUnmount, resetValue, form } = this.props
      const { shouldValidateOnChange } = this.state
      resetOnUnmount && this.onChange(name, resetValue)
      form.setFieldTouched(name, false, shouldValidateOnChange)
    }

    onChange = (name, value) => {
      const { form, onChange, ...props } = this.props
      const { shouldValidateOnChange } = this.state
      if (typeof onChange === 'function') {
        onChange({ name, value, shouldValidateOnChange, ...form, ...props })
      } else {
        form.setFieldValue(name, value, shouldValidateOnChange)
      }
    }

    onBlur = () => {
      const { name, onBlur, form, ...props } = this.props
      const { value, shouldValidateOnBlur } = this.state
      if (typeof onBlur === 'function') {
        onBlur({ name, value, shouldValidateOnBlur, ...form, ...props })
      } else {
        form.setFieldTouched(name, !!value, shouldValidateOnBlur)
      }
    }

    renderError = () => {
      const { fieldError, fieldTouched } = this.state
      return fieldError && fieldTouched && <FormError>{fieldError}</FormError>
    }

    render () {
      return (
        <Component
          {...this.props}
          {...this.state}
          onBlur={this.onBlur}
          onChange={this.onChange}
          renderError={this.renderError}
        />
      )
    }
  }
)

const FormErrorElement = styled(P).attrs(() => ({
  marginTop: marginSizes.extraSmall
}))`
  color: ${colorConstants.red};
`

export const FormError = withFormContext(class extends React.PureComponent {
  static propTypes = {
    innerRef: PropTypes.func,
    disableAutoScroll: PropTypes.bool,
    form: PropTypes.object.isRequired // injected from withFormContext
  }

  static defaultProps = {
    disableAutoScroll: false
  }

  scrollIntoView = () => {
    if (this.element && this.element.scrollIntoView) {
      this.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  componentDidMount () {
    this.scrollIntoView()
  }

  componentDidUpdate (prevProps) {
    const { form, disableAutoScroll } = this.props
    const { isValidating, isValid, isSubmitting } = form
    // Only scroll into view if validation is done but failed and the form is still submitting
    if (!disableAutoScroll && prevProps.form.isValidating === true && isValidating === false && isValid === false && isSubmitting === true) {
      this.scrollIntoView()
    }
  }

  setRef = (e) => {
    if (e) {
      this.element = e
    }
    if (this.props.innerRef) {
      this.props.innerRef(e)
    }
  }

  render () {
    return (
      <FormErrorElement {...this.props} ref={this.setRef} />
    )
  }
})

const InputContainer = styled(Div).attrs(() => ({ marginBottom: marginSizes.medium }))`
  width: 100%;
`

const TextArea = styled.textarea`
  ${inputStyle};
  border: none;
  height: 140px;
  resize: vertical;
  background-color: ${props => props.disabled ? colorConstants.lightestGray : 'transparent'};
`

export const FormTextAreaLabel = styled(LabelElement).attrs(
  (props) => ({
    line: props.focus ? 1 : undefined
  })
)`
  top: 1em;
  transform: scale(1);
  /* stylelint-disable declaration-colon-newline-after */
  ${props => props.focus ? `
    top: 0em;
    margin-top: ${margin[marginSizes.small]};;
    transform: scale(${formLabelScaleRatio});
  ` : ''};
  /* stylelint-enable */
`

export const FormInput = makeFormField(({ Container = InputContainer, renderError, fieldError, fieldTouched, ...props }) => (
  <Container>
    <Input {...props} withError={fieldError && fieldTouched} controlled />
    {renderError()}
  </Container>
))

export const FormSimpleInput = makeFormField(({ Container = InputContainer, renderError, fieldError, fieldTouched, ...props }) => (
  <Container>
    <SimpleInput {...props} withError={fieldError && fieldTouched} controlled />
    {renderError()}
  </Container>
))

export const FormTextArea = makeFormField(({ renderError, LabelComponent = FormTextAreaLabel, ...props }) => (
  <InputContainer>
    <Input Element={TextArea} LabelComponent={LabelComponent} {...props} controlled />
    {renderError()}
  </InputContainer>
))

// Make submit button to full width by default if props.width is not provided.
export const SubmitButton = withFormContext(({ form, disabled, ...props }) => (
  <Button type='submit' disabled={disabled || form.isSubmitting} loading={form.isSubmitting} text='Submit' onClick={form.handleSubmit} {...props} />
))

export const FormCheckbox = makeFormField(({ renderError, value, ...props }) => (
  <React.Fragment>
    <Checkbox checked={value} {...props} controlled />
    {renderError()}
  </React.Fragment>
))

FormCheckbox.Tick = Checkbox.Tick

export const FormRadioGroup = makeFormField(({ value, selected, ...props }) => (
  <RadioGroup selected={value || selected} {...props} controlled />
))

export const FormRadio = Radio
FormRadio.Dot = RadioDot

export const FormDatePicker = makeFormField(({ value, ...props }) => (
  <DatePicker selectedDays={value} {...props} />
))

export const FormSimpleDatePicker = makeFormField(({ value, ...props }) => (
  <SimpleDatePicker selectedDay={value} {...props} />
))

export const FormDropdownDatePicker = makeFormField(({ Container = InputContainer, renderError, value, fieldError, fieldTouched, ...props }) => (
  <Container>
    <DropdownDatePicker {...props} selectedDate={value} withError={fieldError && fieldTouched} />
    {renderError()}
  </Container>
))

export const FormQuantitySelector = makeFormField(({ form, ...props }) => (
  <QuantitySelector {...props} controlled />
))

export const FormPhone = withRoute(makeFormField(({ Container = InputContainer, route, fieldError, fieldTouched, renderError, ...props }) => (
  <Container>
    {/* TODO: use parseLocale helper */}
    <TelephoneInput defaultCountryCode={route.locale && route.locale.split('_')[1].toLowerCase()} withError={fieldError && fieldTouched} {...props} controlled />
    {renderError()}
  </Container>
)))

export const FormPassword = makeFormField(({ Container = InputContainer, renderError, fieldError, fieldTouched, ...props }) => (
  <Container>
    <PasswordInput {...props} withError={fieldError && fieldTouched} controlled />
    {renderError()}
  </Container>
))

export const FormNumber = makeFormField(({ Container = InputContainer, renderError, fieldError, fieldTouched, ...props }) => (
  <Container>
    <NumberInput {...props} withError={fieldError && fieldTouched} controlled />
    {renderError()}
  </Container>
))

export const FormAddressSearchInput = makeFormField(({ fieldError, fieldTouched, renderError, children, form, ...props }) => (
  <AddressSearchInput withError={fieldError && fieldTouched} {...form} {...props} controlled>
    {({ renderSearchInput, ...searchState }) => (
      <React.Fragment>
        {renderSearchInput()}
        {renderError()}
        {typeof children === 'function' ? children(searchState) : children}
      </React.Fragment>
    )}
  </AddressSearchInput>
))

export const FormSelect = makeFormField(({ value, fieldError, fieldTouched, renderError, ...props }) => (
  <React.Fragment>
    <Select {...props} withError={fieldError && fieldTouched} selectedOption={value || {}} controlled />
    {renderError()}
  </React.Fragment>
))

export const FormSelectWithLabel = makeFormField(({ Container = InputContainer, value, renderError, fieldError, fieldTouched, ...props }) => (
  <Container>
    <SelectWithLabel {...props} withError={fieldError && fieldTouched} selectedOption={value || {}} controlled />
    {renderError()}
  </Container>
))

export const ErrorMessage = withFormContext(({ form: { errors, touched }, children }) => (
  children({ errors, touched }) ? (
    <Div marginBottom={marginSizes.small}>
      <InlineNotification lightTheme state={notificationStates.error} text={children({ errors, touched })} />
    </Div>
  ) : null
))

export const FormRef = React.forwardRef((_, ref) => {
  const form = React.useContext(FormContext)

  React.useEffect(() => {
    ref.current = form
  }, [form, ref])

  return null
})

export const Effect = withFormContext(
  class FormEffect extends React.PureComponent {
    static propTypes = {
      onChange: PropTypes.func,
      form: PropTypes.object
    }

    static defaultProps = {
      onChange: () => {}
    }

    componentDidUpdate ({ form: prevForm }) {
      const { form } = this.props
      if (prevForm.values !== form.values) {
        this.props.onChange(prevForm, form)
      }
    }

    render () {
      return null
    }
  }
)
